import { api } from '@/lib/api';
import {
  selectAllAccounts,
  selectCurrentAccountId,
  selectRootAccounts,
} from '@/lib/redux/app.selectors';
import {
  Account,
  setAllAccounts,
  setCurrentAccountId,
  setSubAccountsByRoot,
} from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import useAccountSwitching from '@/shared/hooks/useAccountSwitching';
import { isParentChainComplete } from '@/shared/utils/accountHierarchy';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook for managing account data fetching and state management
 * specifically for account selection UI components.
 *
 * This hook is different from the pure data fetching hooks in `containers/Accounts/api.ts`
 * because it includes business logic for account selection, UI state management,
 * and Redux integration.
 *
 * Handles:
 * - Fetching account details for all root accounts
 * - Fetching sub-accounts for all root accounts
 * - Processing and flattening account data for UI consumption
 * - Updating Redux state with processed account data
 * - Initializing current account selection logic
 * - Providing UI-specific loading and error states
 *
 * @returns Object containing account data and loading states for account selection UI
 *
 * @example
 * ```tsx
 * const AccountSelector = () => {
 *   const { isLoading, hasErrors } = useAccountSelectorData();
 *   // Component automatically gets all account data in Redux
 * };
 * ```
 */
export const useAccountSelectorData = () => {
  const dispatch = useAppDispatch();
  const rootAccounts = useSelector(selectRootAccounts);
  const currentAccountId = useSelector(selectCurrentAccountId);
  const allAccounts = useSelector(selectAllAccounts);
  const { getSelectedAccountId } = useAccountSwitching();

  // Track which parent accounts need to be fetched
  const [missingParentIds, setMissingParentIds] = useState<string[]>([]);

  // Dynamically fetch account details for ALL root accounts
  const rootAccountDetailsQueries = useQueries({
    queries: (rootAccounts || []).map((rootAccountId) => ({
      queryKey: ['fetch-account-details', { accountNumber: rootAccountId }],
      queryFn: () => api.get(`accounts/${rootAccountId}`),
      enabled: !!rootAccountId,
      retry: 0,
    })),
  });

  // Dynamically fetch sub-accounts for ALL root accounts
  const rootSubAccountsQueries = useQueries({
    queries: (rootAccounts || []).map((rootAccountId) => ({
      queryKey: ['fetch-sub-accounts', { rootAccountId }],
      queryFn: () => api.get(`accounts/${rootAccountId}/subaccounts`),
      enabled: !!rootAccountId,
      retry: 0,
    })),
  });

  // Dynamically fetch missing parent accounts (non-blocking, background fetching)
  const parentAccountQueries = useQueries({
    queries: missingParentIds.map((parentId) => ({
      queryKey: ['fetch-account-details', { accountNumber: parentId }],
      queryFn: () => api.get(`accounts/${parentId}`),
      enabled: !!parentId,
      retry: 0,
    })),
  });

  // Use ref to track previous data and prevent unnecessary updates
  const prevDataRef = useRef<string>('');

  // Process and update account data when actual data changes
  useEffect(() => {
    if (!rootAccounts || rootAccounts.length === 0) return;

    // Create a snapshot of the current data
    const currentDataSnapshot = JSON.stringify({
      rootAccounts,
      accountDetails: rootAccountDetailsQueries.map((q) => q.data?.data?.account_id),
      subAccounts: rootSubAccountsQueries.map((q) => q.data?.data?.subs),
    });

    // Only proceed if data has actually changed
    if (currentDataSnapshot === prevDataRef.current) return;
    prevDataRef.current = currentDataSnapshot;

    const accounts: Account[] = [];
    const subAccountsByRoot: Record<
      string,
      { account_id: string; account_name: string }[]
    > = {};

    // Add root accounts with their actual names from all queries
    rootAccounts.forEach((rootAccountId, index) => {
      const accountDetailsQuery = rootAccountDetailsQueries[index];
      const accountDetails = accountDetailsQuery?.data?.data;

      accounts.push({
        account_id: rootAccountId,
        account_name: accountDetails?.account_name || rootAccountId,
        account_type: 'root',
        parent_account_id: accountDetails?.parent_account_id || undefined,
        parent_account_key: accountDetails?.parent_account_key || undefined,
      });
    });

    // Add sub-accounts from all completed queries
    rootAccounts.forEach((rootAccountId, index) => {
      const subAccountsQuery = rootSubAccountsQueries[index];
      const subAccountsResponse = subAccountsQuery?.data?.data;

      if (subAccountsResponse?.subs) {
        const subs = subAccountsResponse.subs;
        subAccountsByRoot[rootAccountId] = subs;

        subs.forEach((sub: { account_id: string; account_name: string }) => {
          accounts.push({
            account_id: sub.account_id,
            account_name: sub.account_name,
            account_type: 'sub',
            parent_account_id: rootAccountId,
            // Note: parent_account_key will be fetched when account details are loaded
          });
        });
      }
    });

    // Only update if we have valid data
    if (accounts.length > 0) {
      dispatch(setAllAccounts(accounts));
      dispatch(setSubAccountsByRoot(subAccountsByRoot));
    }
  }); // No dependency array - this effect runs on every render but uses ref to prevent unnecessary work

  // Initialize current account selection
  useEffect(() => {
    if (!rootAccounts || currentAccountId) return;

    const selectedAccountId = getSelectedAccountId(rootAccounts);
    if (selectedAccountId) {
      dispatch(setCurrentAccountId(selectedAccountId));
    }
  }, [rootAccounts, currentAccountId, getSelectedAccountId, dispatch]);

  // Check for missing parent accounts when current account changes
  // This is non-blocking - we update missingParentIds to trigger background fetching
  useEffect(() => {
    if (!currentAccountId || !allAccounts) return;

    const { isComplete, missingParentIds: missing } = isParentChainComplete(
      currentAccountId,
      allAccounts
    );

    if (!isComplete && missing.length > 0) {
      setMissingParentIds(missing);
    } else {
      // Clear missing parents if chain is now complete
      if (missingParentIds.length > 0) {
        setMissingParentIds([]);
      }
    }
  }, [currentAccountId, allAccounts, missingParentIds.length]);

  // Track previous parent fetches to prevent unnecessary updates
  const prevParentDataRef = useRef<string>('');

  // Update allAccounts when parent account data is fetched
  useEffect(() => {
    if (parentAccountQueries.length === 0 || !allAccounts) return;

    // Create a snapshot of current parent data
    const currentParentSnapshot = JSON.stringify(
      parentAccountQueries.map((q) => ({
        accountId: q.data?.data?.account_id,
        isSuccess: q.isSuccess,
      }))
    );

    // Only proceed if data has changed
    if (currentParentSnapshot === prevParentDataRef.current) return;
    prevParentDataRef.current = currentParentSnapshot;

    // Check if we have any successful parent account fetches
    const fetchedParents = parentAccountQueries
      .filter((query) => query.isSuccess && query.data?.data)
      .map((query) => {
        const accountData = query.data!.data;
        return {
          account_id: accountData.account_id,
          account_name: accountData.account_name,
          account_type: accountData.parent_account_id
            ? ('sub' as const)
            : ('root' as const),
          parent_account_id: accountData.parent_account_id || undefined,
          parent_account_key: accountData.parent_account_key || undefined,
        };
      });

    if (fetchedParents.length > 0) {
      // Merge fetched parents into allAccounts
      const updatedAccounts = [...allAccounts];
      fetchedParents.forEach((parent) => {
        // Only add if not already in the list
        if (!updatedAccounts.find((acc) => acc.account_id === parent.account_id)) {
          updatedAccounts.push(parent);
        }
      });

      dispatch(setAllAccounts(updatedAccounts));
    }
  }); // No dependency array - runs every render but uses ref to prevent unnecessary work

  // Calculate loading states
  const isLoadingAccountDetails = rootAccountDetailsQueries.some(
    (query) => query.isLoading
  );
  const isLoadingSubAccounts = rootSubAccountsQueries.some((query) => query.isLoading);
  const isLoading = isLoadingAccountDetails || isLoadingSubAccounts;

  // Calculate error states
  const accountDetailsErrors = rootAccountDetailsQueries.filter((query) => query.error);
  const subAccountsErrors = rootSubAccountsQueries.filter((query) => query.error);
  const hasErrors = accountDetailsErrors.length > 0 || subAccountsErrors.length > 0;

  return {
    // Loading states
    isLoading,
    isLoadingAccountDetails,
    isLoadingSubAccounts,

    // Error states
    hasErrors,
    accountDetailsErrors,
    subAccountsErrors,

    // Raw query results (if needed for advanced usage)
    rootAccountDetailsQueries,
    rootSubAccountsQueries,
  };
};
